<template>
    <div class="keep-alive-widget-manager">
        <!-- WhatsApp Chat Starter Modal -->
        <Modal2
            v-if="showWhatsappChatStarterModal"
            @cancel="showWhatsappChatStarterModal = false"
            :title="$t('message.whatsappChatStart')"
            :showFooter="false"
        >
            <WhatsappChatStarter
                :default-phone-number="defaultPhoneNumber"
                phone-number-override
                @close="showWhatsappChatStarterModal = false"
            />
        </Modal2>

        <!-- Keep-alive widget overlay -->
        <div 
            v-if="activeWidget" 
            class="keep-alive-widget-overlay" 
            :class="{ 'full-screen': activeWidget.fullScreen }"
        >
            <div class="keep-alive-widget-container">
                <div class="keep-alive-widget-header">
                    <h3>{{ activeWidget.title }}</h3>
                    <a-button type="link" class="close-button" @click="closeWidget">
                        <a-icon type="close" />
                    </a-button>
                </div>
                <div class="keep-alive-widget-content">
                    <!-- Aktif iframe buraya yerleşecek (CSS ile) -->
                </div>
            </div>
        </div>

        <!-- Keep-alive iframe container (her zaman DOM'da kalır) -->
        <div 
            class="keep-alive-iframe-container" 
            :class="{ 'full-screen-mode': activeWidget && activeWidget.fullScreen }"
        >
            <iframe
                v-for="widget in keepAliveWidgets"
                :key="widget.webTabId"
                :src="widget.url"
                :class="{ 'active': activeWidget && activeWidget.webTabId === widget.webTabId }"
                frameborder="0"
                allow="camera; microphone; geolocation; fullscreen; autoplay; payment; vr; web-share; accelerometer; gyroscope; magnetometer; picture-in-picture; display-capture; clipboard-read; clipboard-write; usb; bluetooth; serial; hid; speaker-selection; encrypted-media; midi; sync-xhr"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-pointer-lock allow-orientation-lock allow-modals allow-downloads allow-popups-to-escape-sandbox allow-presentation allow-storage-access-by-user-activation allow-top-navigation allow-top-navigation-by-user-activation"
            ></iframe>
        </div>
    </div>
</template>

<script>
import SYSTEM_PUBSUB from "../../utils/systemPubSub";
import { filter } from "rxjs/operators";
import { mapGetters, mapActions } from "vuex";
import WhatsappChatStarter from "../../views/Pages/Integrations/meta-leads/WhatsappChatStarter.vue";
import Modal2 from "./Modal2.vue";

const COMMANDS = {
    CLOSE_SUPSIS_EXTERNAL_WIDGET: "close-supsis-external-widget",
    OPEN_URL_IN_NEW_TAB: "open_url_in_new_tab",
    VIEW_RECORD: "view_record",
    START_CHAT: "start_chat",
    CREATE_TASK: "create_task",
    SHOW_WINDOW: "show_window",
    SHOW_CONTACT: "show_contact",
    SHOW_MESSAGE: "show_message",
    MAKE_CALL: "make_call",
};

export default {
    name: "KeepAliveWidgetManager",
    components: {
        Modal2,
        WhatsappChatStarter,
    },
    data() {
        return {
            keepAliveWidgets: [],
            activeWidget: null,
            subscriptions: [],
            showWhatsappChatStarterModal: false,
            defaultPhoneNumber: "",
        };
    },
    computed: {
        ...mapGetters({
            getWebTabUrl: "webtabs/getWebTabUrl",
        }),
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
        }),
        openKeepAliveWidget(payload) {
            const { webTabId, title, fullScreen, webTab } = payload;

            if (!webTabId) return;

            // URL'yi generate et
            const url = this.getWebTabUrl(webTab);
            if (!url) {
                console.error("WebTab URL could not be generated");
                return;
            }

            // Mevcut widget var mı kontrol et
            const existingWidget = this.keepAliveWidgets.find((w) => w.webTabId === webTabId);

            if (existingWidget) {
                // Mevcut widget'ı göster
                this.activeWidget = {
                    ...existingWidget,
                    fullScreen: fullScreen || false,
                };
            } else {
                // Yeni keep-alive widget oluştur
                const newWidget = {
                    webTabId,
                    title,
                    url,
                    fullScreen: fullScreen || false,
                };
                this.keepAliveWidgets.push(newWidget);
                this.activeWidget = newWidget;
            }
        },
        closeWidget() {
            // Sadece overlay'i kapat, iframe arka planda kalsın
            this.activeWidget = null;
        },
        handleWidgetMessage(event) {
            if (event.data && event.data.type === COMMANDS.CLOSE_SUPSIS_EXTERNAL_WIDGET) {
                this.closeWidget();
            } else if (event.data && event.data.type === COMMANDS.VIEW_RECORD) {
                const { title, tableId, recordId, multiple, autoClose } = event.data;
                this.addNewWindow({
                    id: recordId,
                    type: "table-record-viewer",
                    title,
                    payload: {
                        tableId,
                        recordId,
                    },
                    multiple,
                    autoClose,
                });
            } else if (event.data && event.data.type === COMMANDS.START_CHAT) {
                const { phoneNumber, close } = event.data;
                this.defaultPhoneNumber = phoneNumber;
                this.showWhatsappChatStarterModal = true;
                if (close) this.closeWidget();
            } else if (event.data && event.data.type === COMMANDS.CREATE_TASK) {
                this.addNewWindow({
                    type: "chat-create-task",
                    title: this.$t("task.createTaskMessage"),
                });
            } else if (event.data && event.data.type === COMMANDS.SHOW_WINDOW) {
                const { type, title, payload, footer } = event.data;
                this.addNewWindow({
                    type,
                    title,
                    payload,
                    footer,
                });
            } else if (event.data && event.data.type === COMMANDS.SHOW_CONTACT) {
                const { title, contactId } = event.data;
                this.addNewWindow({
                    type: "show-visitor",
                    title,
                    payload: {
                        visitorId: contactId,
                    },
                    footer: false,
                });
            } else if (event.data && event.data.type === COMMANDS.SHOW_MESSAGE) {
                const { notificationType, message, description } = event.data;
                this.$notification[notificationType]({
                    message,
                    description,
                });
            } else if (event.data && event.data.type === COMMANDS.MAKE_CALL) {
                const { phoneNumber, close } = event.data;
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.WEBPHONE_CALL_START, {
                    phoneNumber,
                });
                if (close) this.closeWidget();
            } else if (event.data && event.data.type === COMMANDS.OPEN_URL_IN_NEW_TAB) {
                if (event.data.url && event.data.url !== "#") {
                    window.open(event.data.url, "_blank");
                }
            }
        },
    },
    mounted() {
        // OPEN_KEEP_ALIVE_WIDGET event'ini dinle
        const openSub = SYSTEM_PUBSUB.stream
            .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.OPEN_KEEP_ALIVE_WIDGET))
            .subscribe((x) => {
                this.openKeepAliveWidget(x.payload);
            });

        this.subscriptions.push(openSub);

        // Widget'tan gelen mesajları dinle
        window.addEventListener("message", this.handleWidgetMessage);
    },
    beforeDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        window.removeEventListener("message", this.handleWidgetMessage);
    },
};
</script>

<style lang="scss" scoped>
.keep-alive-widget-manager {
    // Keep-alive widget overlay
    .keep-alive-widget-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 1000;
        display: flex;
        justify-content: center;
        align-items: center;

        &.full-screen {
            .keep-alive-widget-container {
                width: 100%;
                height: 100%;
                max-width: 100%;
                max-height: 100%;
                border-radius: 0;
            }
        }
    }

    .keep-alive-widget-container {
        background-color: #fff;
        border-radius: 4px;
        width: 90%;
        max-width: 1200px;
        height: 90vh;
        display: flex;
        flex-direction: column;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    .keep-alive-widget-header {
        padding: 16px 24px;
        border-bottom: 1px solid #f0f0f0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;

        h3 {
            margin: 0;
            font-size: 16px;
            color: rgba(0, 0, 0, 0.85);
        }

        .close-button {
            padding: 0;
            font-size: 16px;
        }
    }

    .keep-alive-widget-content {
        flex: 1;
        position: relative;
        overflow: hidden;
    }

    // Keep-alive iframe container
    .keep-alive-iframe-container {
        position: fixed;
        top: 0;
        left: 0;
        width: 0;
        height: 0;
        overflow: visible;
        pointer-events: none;
        z-index: 1001;

        iframe {
            position: absolute;
            width: 1px;
            height: 1px;
            opacity: 0;
            pointer-events: none;
            border: none;
            background: white;

            &.active {
                position: fixed;
                top: calc(5vh + 57px);
                left: 50%;
                transform: translateX(-50%);
                width: 90%;
                max-width: 1200px;
                height: calc(90vh - 57px);
                opacity: 1;
                pointer-events: auto;
                z-index: 1001;
                border-radius: 0 0 4px 4px;
            }
        }

        // Full-screen modunda iframe pozisyonu
        &.full-screen-mode iframe.active {
            top: 57px;
            left: 0;
            transform: none;
            width: 100%;
            max-width: 100%;
            height: calc(100vh - 57px);
            border-radius: 0;
        }
    }
}
</style>

